// Excel files for IIL employee advances:
//  - the payment file finance uses to pay an advance (complete employee, bank
//    and advance details, one row per advance), and
//  - a full ledger export of every advance with its payroll recoveries.
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { normalizeCode } from "./iilAdvanceCalculator";

export const PAYMENT_COLUMNS = [
  "Sr", "Employee Code", "Safdar Emp ID", "Employee Name", "Father Name", "CNIC", "Segment", "Department", "Designation",
  "Bank", "Account Number", "Payment Mode", "Amount (PKR)", "Narration", "Advance Date", "Requested By / Reference",
  "Recovery Plan", "Note", "Employee Total Outstanding (after this advance)", "Entered By", "Entered On", "Advance ID"
];

const stamp = (value) => {
  const date = value?.toDate ? value.toDate() : value instanceof Date ? value : null;
  return date ? date.toLocaleString("en-GB") : "";
};

export const recoveryPlanText = (advance) => (advance.installment ? `PKR ${Number(advance.installment).toLocaleString("en-PK")} per payroll` : "All at the next payroll");

export function paymentRows(advances, outstandingByCode = new Map()) {
  return advances.map((advance, index) => [
    index + 1, advance.employeeCode, advance.clientEmpId || "", advance.employeeName, advance.fatherName || "", advance.cnic || "",
    advance.segment || "", advance.department || "", advance.designation || "", advance.bank || "", advance.account || "",
    advance.pmtMode || "", Number(advance.amount || 0), `Salary advance to ${advance.employeeName}`, advance.givenDate,
    advance.emailRef || "", recoveryPlanText(advance), advance.note || "",
    outstandingByCode.has(normalizeCode(advance.employeeCode)) ? outstandingByCode.get(normalizeCode(advance.employeeCode)) : "",
    advance.createdByEmail || "", stamp(advance.createdAt), advance.id
  ]);
}

export function buildPaymentWorkbook(advances, outstandingByCode = new Map()) {
  const rows = paymentRows(advances, outstandingByCode);
  const total = advances.reduce((sum, advance) => sum + Number(advance.amount || 0), 0);
  const amountColumn = PAYMENT_COLUMNS.indexOf("Amount (PKR)");
  const totalRow = PAYMENT_COLUMNS.map(() => "");
  totalRow[amountColumn - 1] = "Total";
  totalRow[amountColumn] = total;
  totalRow[amountColumn + 1] = `${advances.length} payment(s)`;
  const sheet = XLSX.utils.aoa_to_sheet([PAYMENT_COLUMNS, ...rows, [], totalRow]);
  sheet["!cols"] = PAYMENT_COLUMNS.map((name) => ({ wch: Math.max(14, Math.min(30, name.length + 2)) }));
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, "Advance Payments");
  return workbook;
}

function save(workbook, name) {
  saveAs(new Blob([XLSX.write(workbook, { bookType: "xlsx", type: "array" })], { type: "application/octet-stream" }), name.replace(/[^A-Za-z0-9._-]+/g, "_"));
}

export function downloadPaymentFile(advances, label = "", outstandingByCode = new Map()) {
  save(buildPaymentWorkbook(advances, outstandingByCode), `IIL_Advance_Payments_${label ? `${label}_` : ""}${new Date().toISOString().slice(0, 10)}.xlsx`);
}

// Everything ever recorded: advances with status, recoveries by payroll month,
// and what each employee still owes.
export function buildLedgerWorkbook({ advances, recoveries, positions, balances }) {
  const positionOf = new Map(positions.map((position) => [position.advance.id, position]));
  const advanceSheet = XLSX.utils.aoa_to_sheet([
    ["Advance ID", "Date Given", "Employee Code", "Employee Name", "Segment", "CNIC", "Bank", "Account Number", "Amount (PKR)", "Recovery Plan",
      "Recovered (PKR)", "Outstanding (PKR)", "Status", "Payment Status", "Paid On", "Bank Reference", "Payment File Downloads",
      "Requested By / Reference", "Note", "Entered By", "Entered On", "Rejected Reason"],
    ...advances.map((advance) => {
      const position = positionOf.get(advance.id);
      const rejected = advance.entryStatus === "REJECTED";
      return [
        advance.id, advance.givenDate, advance.employeeCode, advance.employeeName, advance.segment || "", advance.cnic || "", advance.bank || "", advance.account || "",
        Number(advance.amount || 0), recoveryPlanText(advance), position ? position.recovered : "", position ? position.outstanding : "",
        rejected ? "Rejected" : position?.status === "CLEARED" ? "Cleared" : position?.status === "PART_RECOVERED" ? "Part recovered" : "Open",
        rejected ? "" : advance.paymentStatus === "PAID" ? "Paid" : "To pay", advance.paidDate || "", advance.bankReference || "", advance.exportCount || 0,
        advance.emailRef || "", advance.note || "", advance.createdByEmail || "", stamp(advance.createdAt), advance.rejectedReason || ""
      ];
    })
  ]);
  const byAdvance = new Map(advances.map((advance) => [advance.id, advance]));
  const recoverySheet = XLSX.utils.aoa_to_sheet([
    ["Payroll Month", "Employee Code", "Employee Name", "Advance ID", "Advance Date", "Recovered (PKR)", "Status", "Posted By", "Posted On"],
    ...[...recoveries].sort((a, b) => String(a.monthKey).localeCompare(String(b.monthKey))).map((row) => [
      row.monthKey, row.employeeCode, row.employeeName, row.advanceId, byAdvance.get(row.advanceId)?.givenDate || "", Number(row.amount || 0),
      row.status === "REVERSED" ? "Reversed" : "Recovered", row.postedByEmail || "", stamp(row.postedAt)
    ])
  ]);
  const balanceSheet = XLSX.utils.aoa_to_sheet([
    ["Employee Code", "Employee Name", "Advanced (PKR)", "Recovered (PKR)", "Outstanding (PKR)"],
    ...balances.map((balance) => [balance.code, balance.name, balance.given, balance.recovered, balance.outstanding])
  ]);
  [advanceSheet, recoverySheet, balanceSheet].forEach((sheet) => { sheet["!cols"] = Array.from({ length: 22 }, () => ({ wch: 18 })); });
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, advanceSheet, "Advances");
  XLSX.utils.book_append_sheet(workbook, recoverySheet, "Payroll Recoveries");
  XLSX.utils.book_append_sheet(workbook, balanceSheet, "Outstanding by Employee");
  return workbook;
}

export function downloadLedgerFile(data) {
  save(buildLedgerWorkbook(data), `IIL_Advances_Complete_${new Date().toISOString().slice(0, 10)}.xlsx`);
}

// Bank details that are needed to actually pay the employee.
export const missingBankDetails = (advance) => [
  !advance.account && "account number",
  !advance.bank && "bank"
].filter(Boolean);
